import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import api from '../services/api';
import { getTemplateTheme } from '../config/templateThemes';

const StoreContext = createContext();

export const useStore = () => {
  const context = useContext(StoreContext);
  if (!context) {
    throw new Error('useStore must be used within a StoreProvider');
  }
  return context;
};

// 預設版型設定
const DEFAULT_THEMES = {
  classic: { primary: '#1976d2', secondary: '#dc004e' },
  warm: { primary: '#e65100', secondary: '#ff9800' },
  elegant: { primary: '#5c6bc0', secondary: '#7e57c2' },
  fresh: { primary: '#26a69a', secondary: '#4db6ac' },
  modern: { primary: '#424242', secondary: '#616161' },
  sweet: { primary: '#ec407a', secondary: '#f48fb1' },
  business: { primary: '#37474f', secondary: '#546e7a' }
};

export const StoreProvider = ({ children }) => {
  const [storeConfig, setStoreConfig] = useState({
    store_name: '',
    store_name_short: '',
    store_name_en: '',
    company_name: '',
    phone: '',
    email: '',
    line_id: '',
    service_hours: '',
    logo_url: '',
    favicon_url: '',
    og_image_url: '',
    description: '',
    slogan: '',
    stats_inventory: '60,000+',
    stats_delivery: '2-3天',
    facebook_url: '',
    instagram_url: '',
    line_url: ''
  });

  const [storeSettings, setStoreSettings] = useState(null); // 完整租戶設定

  const [seoConfig, setSeoConfig] = useState({
    site_name: '優爾商城',
    site_url: '',
    default_title: '優爾商城 | 清倉特賣 超值優惠',
    default_description: '',
    default_keywords: '',
    google_verification: '',
    bing_verification: ''
  });

  const [features, setFeatures] = useState([]);
  const [categories, setCategories] = useState([]);
  const [shippingConfig, setShippingConfig] = useState({
    base_fee: 60,
    free_shipping_threshold: 799
  });

  // 版型相關
  const [themeConfig, setThemeConfig] = useState({
    theme_id: 'warm',
    primary_color: '#e65100'
  });

  const [templateId, setTemplateId] = useState('classic');
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPublicSettings();
  }, []);

  const loadPublicSettings = async () => {
    try {
      // 同時載入舊的設定 API 和新的租戶設定 API
      const requests = [
        api.get('/settings/store/public').catch(() => ({ data: { success: false } })),
        api.get('/settings/seo/public').catch(() => ({ data: { success: false } })),
        api.get('/settings/features/public').catch(() => ({ data: { success: false } })),
        api.get('/settings/categories/public').catch(() => ({ data: { success: false } })),
        api.get('/settings/shipping/public').catch(() => ({ data: { success: false } })),
        api.get('/tenant-settings/public').catch(() => ({ data: { success: false } }))
      ];

      const [storeRes, seoRes, featuresRes, categoriesRes, shippingRes, tenantRes] = await Promise.all(requests);

      // 處理舊的系統設定 (向後相容)
      if (storeRes.data.success) {
        setStoreConfig(prev => ({ ...prev, ...storeRes.data.config }));
      }
      if (seoRes.data.success) {
        setSeoConfig(prev => ({ ...prev, ...seoRes.data.config }));
      }
      if (featuresRes.data.success) {
        setFeatures(featuresRes.data.features || []);
      }
      if (categoriesRes.data.success) {
        setCategories(categoriesRes.data.categories || []);
      }
      if (shippingRes.data.success && shippingRes.data.config) {
        setShippingConfig({
          base_fee: parseInt(shippingRes.data.config.base_fee) || 60,
          free_shipping_threshold: parseInt(shippingRes.data.config.free_shipping_threshold) || 799
        });
      }

      // 處理租戶設定 (優先使用)
      if (tenantRes.data.success) {
        const tenantConfig = tenantRes.data.config || {};
        const tenantTheme = tenantRes.data.theme || {};

        // 儲存完整租戶設定
        setStoreSettings(tenantConfig);

        // 設定租戶資訊
        if (tenantRes.data.tenant) {
          setTenant(tenantRes.data.tenant);
        }

        // 租戶設定優先覆蓋系統設定
        if (tenantConfig.store_name) {
          setStoreConfig(prev => ({
            ...prev,
            store_name: tenantConfig.store_name,
            store_name_short: tenantConfig.store_name_short || tenantConfig.store_name,
            description: tenantConfig.description || prev.description,
            slogan: tenantConfig.slogan || prev.slogan,
            logo_url: tenantConfig.logo_url || prev.logo_url,
            favicon_url: tenantConfig.favicon_url || prev.favicon_url,
            phone: tenantConfig.contact_phone || prev.phone,
            email: tenantConfig.contact_email || prev.email,
            line_id: tenantConfig.line_id || prev.line_id,
            service_hours: tenantConfig.service_hours || prev.service_hours,
            facebook_url: tenantConfig.facebook_url || prev.facebook_url,
            instagram_url: tenantConfig.instagram_url || prev.instagram_url,
            line_url: tenantConfig.line_url || prev.line_url
          }));
        }

        // 設定版型
        setThemeConfig({
          theme_id: tenantConfig.theme_id || 'warm',
          primary_color: tenantTheme.color || tenantConfig.primary_color || '#e65100'
        });

        // 設定模板
        if (tenantConfig.template_id) {
          setTemplateId(tenantConfig.template_id);
        }

        // 租戶運費設定
        if (tenantConfig.base_fee || tenantConfig.free_shipping_threshold) {
          setShippingConfig({
            base_fee: parseInt(tenantConfig.base_fee) || 60,
            free_shipping_threshold: parseInt(tenantConfig.free_shipping_threshold) || 799
          });
        }
      }
    } catch (error) {
      console.error('載入商店設定失敗:', error);
    } finally {
      setLoading(false);
    }
  };

  // 動態建立 MUI 主題 - 根據 templateId 取得配色
  const muiTheme = useMemo(() => {
    const tpl = getTemplateTheme(templateId);
    // 租戶自訂色覆蓋模板預設色
    const primaryColor = themeConfig.primary_color && themeConfig.primary_color !== '#e65100'
      ? themeConfig.primary_color
      : tpl.primary;

    return createTheme({
      palette: {
        mode: tpl.mode,
        primary: {
          main: primaryColor,
          light: tpl.primaryLight,
          dark: tpl.primaryDark
        },
        secondary: {
          main: tpl.secondary
        },
        background: {
          default: tpl.background,
          paper: tpl.paper
        },
        custom: {
          accent: tpl.accent,
          saleBadge: tpl.saleBadge,
          hoverTint: tpl.hoverTint,
          headerGradient: tpl.headerGradient,
          footerBg: tpl.footerBg,
          footerPaper: tpl.footerPaper,
          categoryColors: tpl.categoryColors,
        }
      },
      typography: {
        fontFamily: [
          '-apple-system',
          'BlinkMacSystemFont',
          '"Microsoft JhengHei"',
          '"Segoe UI"',
          'Roboto',
          '"Helvetica Neue"',
          'Arial',
          'sans-serif'
        ].join(','),
      },
      components: {
        MuiButton: {
          styleOverrides: {
            root: {
              borderRadius: 8
            }
          }
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              borderRadius: 8
            }
          }
        }
      }
    });
  }, [themeConfig, templateId]);

  const value = {
    storeConfig,
    storeSettings, // 完整租戶設定
    seoConfig,
    features,
    categories,
    shippingConfig,
    themeConfig,
    templateId,
    setTemplateId,
    tenant,
    loading,
    refreshSettings: loadPublicSettings,
    // 便利方法
    getPrimaryColor: () => themeConfig.primary_color || '#e65100',
    getThemeId: () => themeConfig.theme_id || 'warm'
  };

  return (
    <StoreContext.Provider value={value}>
      <ThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </StoreContext.Provider>
  );
};

// 輔助函數：調整顏色亮度
function adjustColor(color, amount) {
  const hex = color.replace('#', '');
  const num = parseInt(hex, 16);
  let r = (num >> 16) + amount;
  let g = ((num >> 8) & 0x00FF) + amount;
  let b = (num & 0x0000FF) + amount;

  r = Math.max(0, Math.min(255, r));
  g = Math.max(0, Math.min(255, g));
  b = Math.max(0, Math.min(255, b));

  return '#' + ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0');
}

export default StoreContext;
